import RestClientService from "@/core/services/restClient.service";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import { formatString } from "@/core/utils/helpers";
import { APPROVAL_REQUEST_API_PATHS, LIST_SCOPES } from "../constants";
import { FetchApprovalRequestsParams } from "../models/fetchApprovalRequestsParams.request";
import { CreateOrUpdateApprovalDelegationDto } from "../models/approvalDelegation.request";
import {
  ApprovalRequestActionPayload,
  ApprovalRequestCancelPayload,
  ApprovalRequestOverridePayload,
} from "../models/approvalRequestAction.request";

class ApprovalRequestService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  public listApprovalRequests(
    params: FetchApprovalRequestsParams = {},
  ): Promise<ICoreResponse> {
    const { scope, ...query } = params;
    const endpoint =
      scope === LIST_SCOPES.ALL
        ? APPROVAL_REQUEST_API_PATHS.LIST
        : APPROVAL_REQUEST_API_PATHS.ASSIGNED;

    return this.restClient.get(endpoint, query);
  }

  public getApprovalRequestDetail(requestId: string): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(APPROVAL_REQUEST_API_PATHS.DETAIL, requestId),
    );
  }

  public actionApprovalRequest(
    requestId: string,
    body: ApprovalRequestActionPayload,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(APPROVAL_REQUEST_API_PATHS.ACTION, requestId),
      body,
    );
  }

  public overrideApprovalRequest(
    requestId: string,
    body: ApprovalRequestOverridePayload,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(APPROVAL_REQUEST_API_PATHS.OVERRIDE, requestId),
      body,
    );
  }

  public cancelApprovalRequest(
    requestId: string,
    body: ApprovalRequestCancelPayload,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(APPROVAL_REQUEST_API_PATHS.CANCEL, requestId),
      body,
    );
  }

  public getApprovalDelegations(delegatorId: string): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(APPROVAL_REQUEST_API_PATHS.DELEGATIONS, delegatorId),
    );
  }

  public getAssignedApprovalDelegations(
    delegateId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      formatString(APPROVAL_REQUEST_API_PATHS.ASSIGNED_DELEGATIONS, delegateId),
    );
  }

  public createOrUpdateApprovalDelegation(
    delegatorId: string,
    body: CreateOrUpdateApprovalDelegationDto,
  ): Promise<ICoreResponse> {
    return this.restClient.post(
      formatString(APPROVAL_REQUEST_API_PATHS.DELEGATIONS, delegatorId),
      body,
    );
  }

  public deleteApprovalDelegation(
    delegatorId: string,
    publicId: string,
  ): Promise<ICoreResponse> {
    return this.restClient.delete(
      formatString(
        APPROVAL_REQUEST_API_PATHS.DELEGATION,
        delegatorId,
        publicId,
      ),
      "",
    );
  }
}

export default ApprovalRequestService;
